import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { API, formatError } from "@/lib/api";
import { toast } from "sonner";

export default function PaymentPage() {
  const [price, setPrice] = useState(null);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.has_paid) navigate('/dashboard');
    API.get("/price").then(res => setPrice(res.data));
  }, [user, navigate]);

  const handlePayment = async () => {
    setLoading(true);
    try {
      const { data } = await API.post("/payment/create-checkout", {
        origin_url: window.location.origin
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatError(err));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      <div className="pt-28 sm:pt-36 pb-8 sm:pb-20 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Step indicator */}
            <div className="flex items-center justify-center gap-3 mb-8">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-[#A0655A] text-white text-xs flex items-center justify-center font-medium">
                  <Check size={14} />
                </div>
                <span className="text-xs text-[#A0655A]">Konto</span>
              </div>
              <div className="w-8 h-px bg-[#A0655A]" />
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-[#A0655A] text-white text-xs flex items-center justify-center font-medium">2</div>
                <span className="text-xs text-[#A0655A] font-medium">Betalning</span>
              </div>
              <div className="w-8 h-px bg-[#E8E0DB]" />
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-[#E8E0DB] text-[#999] text-xs flex items-center justify-center font-medium">3</div>
                <span className="text-xs text-[#999]">Börja</span>
              </div>
            </div>

            <h1 className="heading-font text-3xl sm:text-4xl font-medium text-[#0A0A0A] mb-4">
              Få tillgång till hela metoden
            </h1>
            <p className="text-[#333333] text-base sm:text-lg mb-10 max-w-md mx-auto leading-relaxed">
              En engångsbetalning ger dig full tillgång till alla tre faserna, övningar, reflektioner och dina personliga anteckningar.
            </p>

            <div className="bg-[#FEFCFA] rounded-2xl p-8 border border-[#E8E0DB] mb-8 max-w-md mx-auto">
              <div className="text-4xl sm:text-5xl font-medium text-[#0A0A0A] mb-1">
                {price?.price} <span className="text-lg sm:text-xl">kr</span>
              </div>
              <p className="text-[#555] text-sm mb-8">Engångsbetalning</p>

              <ul className="text-left space-y-3 mb-8">
                {[
                  "Alla 9 steg med texter och reflektioner",
                  "Guidade övningar kopplade till varje fas",
                  "Psykologiska självtester",
                  "Personlig dagbok",
                  "Incheckning och uppföljning",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-[#1A1A1A] text-sm">
                    <Check className="text-[#A0655A] flex-shrink-0" size={16} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={handlePayment}
                disabled={loading}
                className="w-full bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-full py-6 text-base"
                data-testid="pay-now-btn"
              >
                {loading ? 'Laddar...' : 'Betala nu'}
              </Button>
            </div>

            <p className="text-sm text-[#777]">
              Säker betalning via Stripe — kort, Swish och Klarna.
            </p>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
